import React, { Component } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { isEmpty } from 'lodash';

import 'bulma/css/bulma.min.css';
import './App.scss';
import axios from 'axios';
import { Auth, Hub, Cache } from 'aws-amplify';
import { connect } from 'react-redux';
import i18n from 'i18next';

import {
  getAuthState,
  getTenantState,
  getCareerChoices,
  getDegreeChoices,
  getPreRegistrationFlag,
  getEducationLevelSelector,
  getUserFavorites,
  getTenantSSOFlag,
  getPreRegistrationState,
  getUserTerritory,
  getUserCountry,
  getCareerResults,
  getUserHasRegistered,
} from './store/selectors';
import {
  setUser,
  setAuthStatus,
  getTenantConfig,
  getCurrentUserId,
  getData,
  getTenantCareers,
  setTenantCareers,
  setCookiesConsent,
  getFavoritesByUser,
  setPreRegistrationFlag,
  userLogout,
  noUserReady,
  setRegisterOptions,
  clearRegistrationData,
  setCurrentLocale,
} from './store/actions';
import { checkSocialLogin } from './components/auth/authUtil';
import { getTenantNameFromURL } from './components/utility/routeUtil';
import Routes from './Routes';
import Loading from './components/utility/Loading';
import GDPR from './components/utility/GDPR';
import ErrorPage from './components/utility/ErrorPage';
import WindowTitle from './components/utility/WindowTitle';
import './sass/main.scss';

const WAITING_TIMEOUT_MS = 10000;

class App extends Component {
  oauthTimeoutId = null;

  constructor(props) {
    super(props);

    // Fallback to clear oauth loading flag in case things get out of sync
    this.oauthTimeoutId = setTimeout(() => {
      this.isOauthLoading = false;
    }, WAITING_TIMEOUT_MS);

    this.state = {
      showCookieFooter: false,
    };
  }

  get onboardingUserData() {
    return this.props.userData;
  }

  get userCareerChoices() {
    return this.props.userCareerChoices;
  }

  get userDegreeChoices() {
    return this.props.userDegreeChoices;
  }

  get favorites() {
    return this.props.userFavorites;
  }

  get tenantHasSSO() {
    return this.props.tenantSSOFlag;
  }

  get userCareerResults() {
    return this.props.userCareerResults;
  }

  get isAuthenticated() {
    return this.props.auth.isAuthenticated;
  }

  get careersNotLoaded() {
    return (
      this.isAuthenticated &&
      this.onboardingUserData.user.id &&
      this.userCareerResults.length === 0
    );
  }

  get userHasRegistered() {
    return this.props.userHasRegistered;
  }

  get isLoading() {
    return (
      !this.props.tenant.name ||
      (this.isAuthenticated && !this.props.preregistrationFlag) ||
      (this.isAuthenticated &&
        !this.props.preregistrationFlag &&
        this.careersNotLoaded &&
        this.userHasRegistered) ||
      isEmpty(this.props.degreeChoices) ||
      this.isOauthLoading ||
      this.props.auth.loading
    );
  }

  get isOauthLoading() {
    return localStorage.getItem('amplify-redirected-from-hosted-ui') === 'true';
  }

  set isOauthLoading(val) {
    if (!val) clearTimeout(this.oauthTimeoutId);

    window.localStorage.setItem('amplify-redirected-from-hosted-ui', !!val);
    this.forceUpdate();
  }

  getEducationLevel = () => {
    return this.props.educationLevel;
  };

  getCountry = () => {
    if (this.props.country) return this.props.country;

    if (
      this.onboardingUserData &&
      this.onboardingUserData.user &&
      this.onboardingUserData.user.country
    )
      return this.onboardingUserData.user.country;
  };

  getAgreedToTerms = () => {
    if (this.props.agreedToTerms) {
      return true;
    }
    return false;
  };

  clearCache = async () => {
    Cache.clear();
  };

  handleAuthEvent = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (
        user &&
        (user.username || (user.attributes && user.attributes.identities))
      ) {
        const isSocialAuth = checkSocialLogin(
          user.username,
          user.attributes.identities
        );
        if (isSocialAuth) {
          this.props.setRegisterOptions('social');
        } else {
          this.props.setRegisterOptions('federation');
        }
      }
      await this.props.setUser(user);

      const session = await Auth.currentSession();
      await this.props.setAuthStatus({
        authenticated: true,
        session,
      });

      if (!user) await Auth.signOut({ global: true });
    } catch (error) {
      console.log('New Auth error Staging: ', error);
      this.props.setAuthStatus({ authenticated: false, session: null });
    }
  };

  handleUserData = async () => {
    this.props.setPreRegistrationFlag(false);

    if (this.isAuthenticated) {
      const token = this.props.auth.jwtToken;
      const userId = await this.props.getCurrentUserId(token);

      // Populate the store with a user's previously saved info
      if (userId) {
        await this.props.getData(userId, token);
        await this.props.getFavoritesByUser(userId, token);
      } else {
        await this.props.noUserReady();
      }
    } else {
      await this.props.noUserReady();
    }
  };

  handleTenant = async () => {
    const { tenantName } = { ...getTenantNameFromURL() };

    if (window.location.pathname === `/${tenantName}`) {
      window.location.assign(`${window.location}/landing`);
    } else if (window.location.pathname.substring(1).indexOf('/') === -1) {
      window.location.assign(`${window.location.pathname.substring(1)}/`);
    }

    await this.props.getTenantConfig(tenantName);
  };

  async componentDidMount() {
     // temporary harmless change
    console.log("cache deployment test..");
    
    Hub.listen('auth', async ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          await this.handleAuthEvent();
          break;
        case 'signOut':
          await this.props.userLogout();
          await this.props.setAuthStatus({
            authenticated: false,
            session: null,
          });
          this.props.history.push(
            `/${this.props.tenant.name.toLowerCase()}/login`
          );
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.error('Sign in failure', data);
          break;
        default:
          return;
      }

      this.isOauthLoading = false;
    });

    await this.handleTenant();
    await this.handleAuthEvent();
    await this.handleUserData();
    this.props.setPreRegistrationFlag(true);

    // add to home screen:
    window.addEventListener('beforeinstallprompt', (event) => {
      window.deferredPrompt = event;
    });

    // Get all the tenant careers for later down the line
    const response = await this.props.getTenantCareers(this.props.tenant.id);
    this.props.setTenantCareers(response);
    document
      .querySelector('#manifest')
      .setAttribute(
        'href',
        `/${this.props.tenant.name.toLowerCase()}-manifest.json`
      );

    this.setState({
      showCookieFooter: true,
    });

    // if we get an unauthed response, let's force them to log back in. make this better in the future
    // only worry about calls to our backend (i.e., NOT careeronestop, etc.)
    axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (
          error.response &&
          error.response.status === 401 &&
          error.response.config.url.startsWith(process.env.REACT_APP_API) &&
          !window.location.pathname.includes('/advisor-dashboard')
        ) {
          console.log(
            '%cAXIOS ERROR',
            'font-weight:bold;color:red;',
            error,
            error.response
          );
          this.props.setAuthStatus({
            authenticated: false,
            session: null,
          });
        }

        return Promise.reject(error);
      }
    );

    // Function to get the locale from localStorage
    const getLocaleFromLocalStorage = (tenantName) => {
      return (
        localStorage.getItem(
          `ME3_PERSISTENCE_LOCALE_${tenantName.toLowerCase()}`
        ) || 'en'
      );
    };

    // Function to set the locale in localStorage
    const setLocaleInLocalStorage = (tenantName, locale) => {
      localStorage.setItem(
        `ME3_PERSISTENCE_LOCALE_${tenantName.toLowerCase()}`,
        locale
      );
    };

    // Check if there is a stored locale for the current tenant
    const tenantName = this.props.tenant.name;
    const storedLocale = getLocaleFromLocalStorage(tenantName);

    // Set the locale based on the stored value or default to English
    const selectedLocale = storedLocale || 'en';

    this.props.setCurrentLocale(selectedLocale);
    setLocaleInLocalStorage(tenantName, selectedLocale);
  }

  get userIsFullyRegistered() {
    return (
      this.props.auth.isAuthenticated &&
      !!this.props.onboarding.userId &&
      typeof this.props.onboarding.userId === 'number' &&
      !!this.props.userData &&
      this.props.userData.careerGameResults.length > 0
    );
  }

  async componentDidUpdate(prevProps) {
    const isAuthenticated = this.props.auth.isAuthenticated;
    const wasAuthenticated = prevProps.auth.isAuthenticated;

    if (wasAuthenticated === isAuthenticated) return;
    await this.handleUserData();
    this.props.setPreRegistrationFlag(true);
  }

  render() {
    if (this.isLoading) return <Loading height="100vh" />;
    const educationLevel = this.getEducationLevel();
    const country = this.getCountry();
    const territory = this.props.territory;
    const agreedToTerms = this.getAgreedToTerms();

    return (
      <>
        <WindowTitle />
        <ErrorPage tenant={this.props.tenant}>
          <Switch>
            <Route
              path={`/${this.props.tenant.name.toLowerCase()}`}
              render={({ match, location }) => (
                <Routes
                  token={this.props.auth.jwtToken}
                  isAuthenticated={this.isAuthenticated}
                  isRogueUser={this.props.auth.isRogueUser}
                  tenant={this.props.tenant}
                  match={match}
                  location={location}
                  degreeChoices={this.props.degreeChoices}
                  userInfo={this.props.userInfo}
                  userRole={
                    this.props.userData.user
                      ? this.props.userData.user.role
                      : undefined
                  }
                  userCareerResults={this.userCareerResults}
                  agreedToTerms={agreedToTerms}
                  educationLevel={educationLevel}
                  userData={this.props.userData}
                  country={country}
                  campusId={this.props.campusId}
                  territory={territory}
                  hasError={this.state.hasError}
                  userScore={this.props.userScore}
                  userHasRegistered={this.props.userHasRegistered}
                  userProfileInfo={this.props.userProfileInfo}
                  userIsFullyRegistered={this.userIsFullyRegistered}
                  registerOptionsValue={
                    this.props.preregistration.registerOptionsValue
                  }
                />
              )}
            />
          </Switch>
        </ErrorPage>
        {this.props.auth.consentedToCookies ||
        this.isAuthenticated ||
        this.props.agreedToTerms ? null : (
          <GDPR
            tenant={this.props.tenant}
            consent={this.props.setCookiesConsent}
            in={this.state.showCookieFooter}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const auth = getAuthState(state);
  const tenant = getTenantState(state);
  const preregistration = getPreRegistrationState(state);
  // onboarding store is too big to pull all of it in, and causes an infinite update when going through onboarding
  // bring in specifics instead.
  const educationLevel = getEducationLevelSelector(state);
  const country = getUserCountry(state);
  const territory = getUserTerritory(state);
  const userCareerChoices = getCareerChoices(state);
  const userDegreeChoices = getDegreeChoices(state);
  const preregistrationFlag = getPreRegistrationFlag(state);
  const userId = state.onboarding.userId;
  const degreeChoices = state.onboarding.degreeChoices;
  const agreedToTerms = state.preregistration.agreedToTerms;
  const userData = state.onboarding.userData;
  const userScore = state.preregistration.score;
  const userFavorites = getUserFavorites(state);
  const tenantSSOFlag = getTenantSSOFlag(state);
  const userCareerResults = getCareerResults(state);
  const userHasRegistered = getUserHasRegistered(state);
  const userProfileInfo = state.admin.userProfileInfo;
  const campusId =
    preregistration?.campusId ||
    (state.onboarding.userData.user && state.onboarding.userData.user.campusId);

  return {
    auth,
    tenant,
    userId,
    degreeChoices,
    agreedToTerms,
    userData,
    educationLevel,
    country,
    campusId,
    territory,
    userScore,
    userCareerChoices,
    userDegreeChoices,
    preregistrationFlag,
    userFavorites,
    tenantSSOFlag,
    preregistration,
    onboarding: state.onboarding,
    userCareerResults,
    userHasRegistered,
    userProfileInfo,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    setAuthStatus,
    setUser,
    getTenantConfig,
    getCurrentUserId,
    getData,
    getTenantCareers,
    setTenantCareers,
    setCookiesConsent,
    getFavoritesByUser,
    setPreRegistrationFlag,
    getEducationLevelSelector,
    userLogout,
    noUserReady,
    setRegisterOptions,
    clearRegistrationData,
    setCurrentLocale,
  })(App)
);
